import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IcClipboardList,
  IcPhone,
  IcPackage,
  IcCalendar,
  IcCheck,
  IcLock,
  IcUnlock,
  IcClose,
  IcTrash,
  IcBanknote,
  IcFlower,
  IcUser,
  IcPackageOpen,
  IcCalculator,
  IcList,
  IcFilters,
} from '../../theme/icons';
import Swal from '../../theme/swal';
import { gated, GATE_CANCELLED } from '../../services/identityGate';
import VenteTabs from '../../components/VenteTabs';
import MobileSoinsCalendar from '../components/MobileSoinsCalendar';
import reservationService from '../../services/reservationService';
import serviceManagementService from '../../services/serviceManagementService';
import customerService from '../../services/customerService';
import PhoneLink from '../../components/PhoneLink';
import KI from '../../theme/palette';

// Product reservation statuses — Étiquettes Ki Para §6 : en attente (vert
// texte) → arrivée (vert texte) → bloquée (aplat encre + cadenas) → vendue
// (aplat vert) / annulée (gris texte).
const STATUS_META = {
  pending: { label: 'En attente', cls: 'bdg-wait' },
  arrived: { label: 'Arrivée', cls: 'bdg-arrive' },
  blocked: { label: 'Bloquée', cls: 'bdg-ink', Icon: IcLock },
  delivered: { label: 'Vendue', cls: 'bdg-done' }, // sold to the client
  cancelled: { label: 'Annulée', cls: 'bdg-cancel' },
};

// Reserved soins are Sessions (rendez-vous) — Étiquettes §7 : programmé /
// en cours / terminé (vert plein) / annulé (gris).
const SESSION_STATUS_META = {
  scheduled: { label: 'Programmé', cls: 'bdg-pos2' },
  in_progress: { label: 'En cours', cls: 'bdg-live' },
  completed: { label: 'Terminé', cls: 'bdg-done' },
  cancelled: { label: 'Annulé', cls: 'bdg-neutral' },
};

const TABS = [
  { key: '', label: 'Toutes' },
  { key: 'pending', label: 'En attente' },
  { key: 'arrived', label: 'Arrivée' },
  { key: 'blocked', label: 'Bloquée' },
  { key: 'delivered', label: 'Vendue' },
  { key: 'cancelled', label: 'Annulée' },
];

const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return d;
  }
};

export default function MobileReservationsList() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('soins'); // 'products' | 'soins' — soins first, like the web version (owner Studio 2026-08-14)
  const [reservations, setReservations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('');
  const [showFilters, setShowFilters] = useState(false); // panneau statuts (mode produits)
  const [soinView, setSoinView] = useState('calendar'); // 'calendar' | 'list' — calendrier par défaut, comme le web
  // Catalogue des soins actifs — alimente le formulaire "Nouvelle réservation"
  // du popup jour (calendrier). Chargé une fois en mode soins.
  const [soinServices, setSoinServices] = useState([]);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const params = activeTab ? { status: activeTab } : {};
      const data = await reservationService.getReservations(params);
      setReservations(Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []));
      setError(null);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Impossible de charger les réservations.');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await serviceManagementService.getAllSessions();
      const list = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      // newest rendez-vous first
      list.sort((a, b) => new Date(b.scheduled_time) - new Date(a.scheduled_time));
      setSessions(list);
      setError(null);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Impossible de charger les soins réservés.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'soins') fetchSessions();
    else fetchReservations();
  }, [mode, fetchReservations, fetchSessions]);

  useEffect(() => {
    if (mode !== 'soins' || soinServices.length > 0) return;
    (async () => {
      try {
        const data = await serviceManagementService.getAllServices();
        const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        setSoinServices(list.filter((s) => s.is_active && s.type === 'SOIN'));
      } catch (err) {
        console.error('Error fetching soin services:', err);
      }
    })();
  }, [mode, soinServices.length]);

  const searchSoinCustomers = async (q) => {
    const data = await customerService.searchCustomers(q);
    return Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
  };

  // Création du rendez-vous depuis le popup jour du calendrier. Retourne true
  // si créé (le formulaire se referme), false sinon (les champs restent remplis).
  const handleCreateSession = async (payload) => {
    try {
      if ((await gated('Qui réserve le soin ?', () => serviceManagementService.createSession(payload))) === GATE_CANCELLED) return;
      Swal.fire({ icon: 'success', title: 'Soin réservé', text: 'Le rendez-vous a été créé.', timer: 1600, showConfirmButton: false });
      fetchSessions();
      return true;
    } catch (err) {
      console.error('Error creating soin reservation:', err);
      // Surface the REAL backend reason instead of a generic message.
      const data = err?.response?.data;
      let msg = data?.error || data?.detail;
      if (!msg && data && typeof data === 'object') {
        const first = Object.values(data)[0];
        msg = Array.isArray(first) ? first[0] : (typeof first === 'string' ? first : null);
      }
      if (!msg && err?.response?.status) msg = `Erreur ${err.response.status}`;
      Swal.fire('Erreur', msg || 'Erreur lors de la réservation du soin.', 'error');
      return false;
    }
  };

  const changeSessionStatus = async (session, status) => {
    try {
      await serviceManagementService.updateSessionStatus(session.id, status);
      fetchSessions();
    } catch (err) {
      console.error('Error updating session:', err);
      Swal.fire('Erreur', 'Impossible de mettre à jour le soin.', 'error');
    }
  };

  const handleDeleteSession = async (session) => {
    const res = await Swal.fire({
      title: 'Supprimer le rendez-vous ?',
      text: `${session.customer_name} — ${session.service_name}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler', confirmButtonColor: KI.coralDark,
    });
    if (!res.isConfirmed) return;
    try {
      await serviceManagementService.deleteSession(session.id);
      fetchSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      Swal.fire('Erreur', 'Impossible de supprimer le soin.', 'error');
    }
  };

  const changeStatus = async (reservation, status) => {
    try {
      if ((await gated('Qui fait ce changement ?', () => reservationService.setStatus(reservation.id, status))) === GATE_CANCELLED) return;
      fetchReservations();
    } catch (err) {
      console.error('Error updating reservation status:', err);
      Swal.fire('Erreur', 'Impossible de mettre à jour la réservation.', 'error');
    }
  };

  // Block the product for the client: deducts the reserved qty from stock (server-side).
  const handleBlock = async (reservation) => {
    const res = await Swal.fire({
      title: 'Bloquer le produit pour le client ?',
      html: `<b>${reservation.quantity}</b> × ${reservation.product_name} sera retiré du stock et réservé pour <b>${reservation.customer_name}</b>.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Bloquer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.spruce,
    });
    if (!res.isConfirmed) return;
    try {
      if ((await gated('Qui bloque le produit ?', () => reservationService.blockReservation(reservation.id))) === GATE_CANCELLED) return;
      fetchReservations();
      Swal.fire({ icon: 'success', title: 'Produit bloqué', timer: 1400, showConfirmButton: false });
    } catch (err) {
      console.error('Error blocking reservation:', err);
      Swal.fire('Erreur', err?.response?.data?.detail || 'Impossible de bloquer le produit.', 'error');
    }
  };

  // Débloquer: give the held stock back and return the reservation to "arrived".
  const handleUnblock = async (reservation) => {
    const res = await Swal.fire({
      title: 'Débloquer le produit ?',
      html: `<b>${reservation.quantity}</b> × ${reservation.product_name} sera remis en stock (réservation « Arrivée »).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Débloquer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.ramps.amber[600],
    });
    if (!res.isConfirmed) return;
    try {
      if ((await gated('Qui débloque le produit ?', () => reservationService.unblockReservation(reservation.id))) === GATE_CANCELLED) return;
      fetchReservations();
      Swal.fire({ icon: 'success', title: 'Produit débloqué', timer: 1400, showConfirmButton: false });
    } catch (err) {
      console.error('Error unblocking reservation:', err);
      Swal.fire('Erreur', err?.response?.data?.detail || 'Impossible de débloquer le produit.', 'error');
    }
  };

  // Vendre au client: open "Nouvelle vente" with the reserved product already in
  // the panier and the reserved client preselected (points/remise apply, other
  // products can be added). Reservation is marked "Vendue" when the sale is finalized.
  const handleSell = async (reservation) => {
    if (!reservation.product) {
      Swal.fire('Produit hors-catalogue', 'Ce produit doit être vendu depuis la page Vente.', 'info');
      return;
    }
    try {
      const prep = await reservationService.prepareSale(reservation.id);
      navigate('/seller-sales', {
        state: {
          addProductId: prep.product_id,
          productQty: prep.quantity,
          clientId: prep.customer_id,
          reservationId: prep.reservation_id,
        },
      });
    } catch (err) {
      console.error('Error preparing reservation sale:', err);
      Swal.fire('Erreur', err?.response?.data?.detail || 'Impossible d’ouvrir la vente.', 'error');
    }
  };

  const handleDelete = async (reservation) => {
    const res = await Swal.fire({
      title: 'Supprimer la réservation ?',
      text: `${reservation.customer_name} — ${reservation.product_name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.coralDark,
    });
    if (!res.isConfirmed) return;
    try {
      if ((await gated('Qui supprime la réservation ?', () => reservationService.deleteReservation(reservation.id))) === GATE_CANCELLED) return;
      fetchReservations();
    } catch (err) {
      console.error('Error deleting reservation:', err);
      Swal.fire('Erreur', 'Impossible de supprimer la réservation.', 'error');
    }
  };

  // Soins are fetched in full, then filtered client-side by the selected case.
  const shownSessions = activeTab ? sessions.filter((s) => s.status === activeTab) : sessions;

  return (
    <div className="w-full px-4 py-6">
      {/* Header retiré (owner Studio 2026-08-14). Bouton déroulant VenteTabs +
          toggle Produits/Soins réservés sur la même ligne, à la place et au
          design du toggle Produits/Services de la page Nouvelle vente (web). */}
      <VenteTabs>
        <div className="inline-flex bg-gray-100 rounded-xl p-1">
          {/* Order Soin → Produits, soins default — same as the web version */}
          <button
            type="button"
            onClick={() => { setMode('soins'); setActiveTab(''); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${mode === 'soins' ? 'bg-white text-gray-900 border border-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <IcFlower size={13} /> Soin
          </button>
          <button
            type="button"
            onClick={() => { setMode('products'); setActiveTab(''); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${mode === 'products' ? 'bg-white text-gray-900 border border-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <IcPackageOpen size={13} /> Produits
          </button>
        </div>
      </VenteTabs>

      {/* Statuts produits regroupés dans un seul bouton « Filtres » (même design
          que la page Produits), placé sous le toggle Soin/Produits. Les chips
          vivent dans le panneau repliable. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {mode === 'products' && (
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`ml-auto px-4 py-2.5 rounded-xl border flex items-center justify-center font-medium transition-colors ${showFilters || activeTab ? 'bg-white text-gray-900 border-gray-900 shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            <IcFilters className="mr-2 text-sm" /> Filtres
          </button>
        )}
        {mode === 'soins' && (
          /* Single toggle button — même design que le web (sans bordure).
             Click switches view; shows the view it will switch TO. */
          <button
            type="button"
            onClick={() => setSoinView((v) => (v === 'calendar' ? 'list' : 'calendar'))}
            className="ml-auto px-4 py-2.5 rounded-xl flex items-center justify-center font-medium transition-colors bg-white text-gray-500 hover:text-gray-700"
          >
            {soinView === 'calendar' ? (
              <><IcList className="mr-2 text-sm" /> Liste</>
            ) : (
              <><IcCalendar className="mr-2 text-sm" /> Calendrier</>
            )}
          </button>
        )}
      </div>

      {mode === 'products' && showFilters && (
        <div className="bg-white border border-gray-200 rounded-2xl mb-4 p-4 flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                activeTab === tab.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded" role="alert">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : mode === 'soins' ? (
        soinView === 'calendar' ? (
          <MobileSoinsCalendar
            sessions={shownSessions}
            statusMeta={SESSION_STATUS_META}
            soinServices={soinServices}
            onSearchCustomers={searchSoinCustomers}
            onCreate={handleCreateSession}
            onComplete={(s) => changeSessionStatus(s, 'completed')}
            onCancel={(s) => changeSessionStatus(s, 'cancelled')}
            onDelete={handleDeleteSession}
          />
        ) : shownSessions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <IcFlower className="mx-auto text-5xl text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">Aucun soin réservé{activeTab ? ' dans ce statut' : ''}.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {shownSessions.map((s) => {
              const meta = SESSION_STATUS_META[s.status] || SESSION_STATUS_META.scheduled;
              return (
                <div key={s.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0"><IcFlower /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-bold text-gray-900 truncate">{s.service_name}</p>
                        <span className={`bdg flex-shrink-0 ${meta.cls} px-2.5 py-0.5 rounded-full`}>{meta.label}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-0.5 inline-flex items-center gap-1"><IcUser size={10} className="text-gray-400" /> {s.customer_name}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><IcCalendar size={10} /> {s.scheduled_time}</span>
                        {Number(s.final_price) > 0 && <span className="inline-flex items-center gap-1"><IcBanknote size={10} /> {Number(s.final_price).toFixed(3)} DT</span>}
                      </div>
                      {s.notes && <p className="text-xs text-gray-500 mt-1.5 italic">“{s.notes}”</p>}
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
                    {s.status === 'scheduled' && (
                      /* Avancement d'état « Terminé » = Bleu #2563EB (Boutons §5). */
                      <button onClick={() => changeSessionStatus(s, 'completed')} className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-1.5"><IcCheck size={11} /> Terminé</button>
                    )}
                    {(s.status === 'scheduled' || s.status === 'in_progress') && (
                      <button onClick={() => changeSessionStatus(s, 'cancelled')} className="btn-g px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5"><IcClose size={11} /> Annuler</button>
                    )}
                    <button onClick={() => handleDeleteSession(s)} className="px-3 py-1.5 rounded-xl text-red-600 hover:bg-red-50 text-xs flex items-center gap-1.5"><IcTrash size={11} /> Supprimer</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : reservations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcClipboardList className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucune réservation{activeTab ? ' dans ce statut' : ''}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reservations.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const arrival = formatDate(r.expected_arrival_date);
            return (
              <div key={r.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-xl bg-gray-100 text-gray-500 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {r.product_image ? (
                      <img src={r.product_image} alt={r.product_name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <IcPackage />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-bold text-gray-900 truncate">{r.product_name}</p>
                      <span className={`bdg flex-shrink-0 ${meta.cls} px-2.5 py-0.5 rounded-full`}>{meta.Icon && <meta.Icon size={11} />}{meta.label}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {r.customer_name}
                      {r.customer_phone && (
                        <span className="text-gray-400 ml-2 inline-flex items-center gap-1"><IcPhone size={10} /> <PhoneLink phone={r.customer_phone} /></span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                      <span>Quantité : <span className="font-semibold text-gray-700">{r.quantity}</span></span>
                      {arrival && <span className="inline-flex items-center gap-1"><IcCalendar size={10} /> Arrivée : {arrival}</span>}
                      {Number(r.deposit_amount) > 0 && (
                        <span className="inline-flex items-center gap-1"><IcBanknote size={10} /> Avance <b className="tabular-nums">{Number(r.deposit_amount).toFixed(3)} DT</b></span>
                      )}
                    </div>
                    {r.note && <p className="text-xs text-gray-500 mt-1.5 italic">“{r.note}”</p>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
                  {r.status === 'pending' && (
                    <button onClick={() => changeStatus(r, 'arrived')} className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-1.5">
                      <IcCheck size={11} /> Marquer arrivée
                    </button>
                  )}
                  {/* Boutons §5 : Bloquer = noir encre #374151 · Débloquer =
                      secondaire · Vendre au client = vert #15803D (primaire). */}
                  {r.status === 'arrived' && (
                    <button onClick={() => handleBlock(r)} className="px-3 py-1.5 rounded-xl bg-gray-700 hover:bg-gray-800 text-white text-xs font-semibold flex items-center gap-1.5">
                      <IcLock size={11} /> Bloquer le produit
                    </button>
                  )}
                  {r.status === 'blocked' && (
                    <button onClick={() => handleUnblock(r)} className="btn-s px-3 py-1.5 rounded-xl text-xs gap-1.5">
                      <IcUnlock size={11} /> Débloquer
                    </button>
                  )}
                  {(r.status === 'arrived' || r.status === 'blocked') && r.product && (
                    <button onClick={() => handleSell(r)} className="btn-p px-3 py-1.5 rounded-xl text-xs gap-1.5">
                      <IcCalculator size={11} /> Vendre au client
                    </button>
                  )}
                  {(r.status === 'pending' || r.status === 'arrived') && (
                    <button onClick={() => changeStatus(r, 'cancelled')} className="btn-g px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5">
                      <IcClose size={11} /> Annuler
                    </button>
                  )}
                  <button onClick={() => handleDelete(r)} className="px-3 py-1.5 rounded-xl text-red-600 hover:bg-red-50 text-xs flex items-center gap-1.5">
                    <IcTrash size={11} /> Supprimer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
